import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { authReducer } from "./Auth/Reducer/auth.reducer";
import { staticReducer } from "./Admin/Reducer/static.reducer";
import { emailReducer } from "./Admin/Reducer/emailTemplate.reducer";
import { userReducer } from "./Admin/Reducer/user.reducer";
import { navCountReducer } from "./Admin/Reducer/nav.reducer";
import { matchReducer } from "./Admin/Reducer/match.reducer";
import { cruiseReducer } from "./Admin/Reducer/cruise.reducer";
import { settingCountReducer } from "./Admin/Reducer/setting.reducer";
import { dashboardReducer } from "./Admin/Reducer/dashboard.reducer";
// import { authReducer } from "./Auth/Redux/Reducers/auth";

// Error Reducer Types
interface ErrorState {
	authError?: Record<string, string>;
}

interface SetErrorActionPayload {
	scope: string;
	status: {
		param: string;
		msg: string;
		path?: string;
		nestedErrors?: { param: string; msg: string }[];
	}[];
}

interface SetErrorAction {
	type: "SET_ERROR";
	payload: SetErrorActionPayload;
}

type ErrorAction = SetErrorAction;

const errorReducer = (state: ErrorState = {}, action: ErrorAction): ErrorState => {
	switch (action.type) {
		case "SET_ERROR": {
			const temp: Record<string, string> = {};
			const { payload } = action;

			if (payload.status && payload.status.length) {
				payload.status.forEach((ele) => {
					if (ele.param === "_error") {
						ele.nestedErrors?.forEach((ele1) => {
							const key = ele1.param.split(".").pop()!;
							temp[key] = ele1.msg;
						});
					} else if (ele.param?.includes("updates.")) {
						const key = ele.param.split(".").slice(1).join(".");
						temp[key] = ele.msg;
					} else {
						temp[ele.path!] = ele.msg;
					}
				});
			}

			return { ...state, [`${payload.scope}Error`]: temp };
		}
		default:
			return state;
	}
};

// Loading Reducer Types
interface LoadingState {
	authLoading: boolean;
	welcomeDataLoading: boolean;
	[key: string]: boolean;
}

interface SetLoadingActionPayload {
	scope: string;
	status: boolean;
}

interface SetLoadingAction {
	type: "SET_LOADING";
	payload: SetLoadingActionPayload;
}

type LoadingAction = SetLoadingAction;

const loadingReducer = (state: LoadingState = { authLoading: false, welcomeDataLoading: false }, action: LoadingAction): LoadingState => {
	switch (action.type) {
		case "SET_LOADING":
			return {
				...state,
				[`${action.payload.scope}Loading`]: action.payload.status,
			};
		default:
			return state;
	}
};

// Msg Reducer Types
interface MsgState {
	success?: string;
	error?: string;
	[key: string]: string | undefined;
}

interface SetToastActionPayload {
	scope: string;
	status?: string;
}

interface SetToastAction {
	type: "SET_TOAST";
	payload: SetToastActionPayload;
}

interface ClearToastAction {
	type: "CLEAR_TOAST";
}

type MsgAction = SetToastAction | ClearToastAction;

const msgReducer = (state: MsgState = {}, action: MsgAction): MsgState => {
	switch (action.type) {
		case "SET_TOAST":
			return {
				...state,
				[`${action.payload.scope}`]: action.payload.status || "Something went wrong!",
			};
		case "CLEAR_TOAST":
			return { ...state, error: undefined, success: undefined };
		default:
			return state;
	}
};

// Combine reducers
const rootReducer = combineReducers({
	error: errorReducer,
	loading: loadingReducer,
	msg: msgReducer,

	auth: authReducer,
	static: staticReducer,
	email: emailReducer,
	user: userReducer,
	cruise: cruiseReducer,
	match: matchReducer,

	//nav
	nav: navCountReducer,
	setting: settingCountReducer,
	dashboard: dashboardReducer,
});

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export interface RootState {
	msg: MsgState;
	error: ErrorState;
	loading: LoadingState;
}

// Configure store
const store = configureStore({
	reducer: rootReducer,
});

export default store;
